import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  FileText, Plus, Trash2, Edit, Eye, GripVertical, ToggleLeft, ToggleRight,
  ChevronDown, ChevronUp, ClipboardList, X, Check, Send, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'textarea', label: 'Texto Largo' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Fecha' },
  { value: 'select', label: 'Selección' },
  { value: 'multiselect', label: 'Selección Múltiple' },
  { value: 'checkbox', label: 'Casilla (Sí/No)' },
  { value: 'signature', label: 'Firma' },
  { value: 'file', label: 'Subir Archivo' },
];

const CATEGORIES = [
  { value: 'enrollment', label: 'Inscripción/Matrícula' },
  { value: 'survey', label: 'Encuesta/Evaluación' },
  { value: 'admin', label: 'Administrativo' },
  { value: 'general', label: 'General' },
];

const FormsTab = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, builder, fill, responses
  const [selectedForm, setSelectedForm] = useState(null);
  const [responses, setResponses] = useState([]);

  // Builder state
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCategory, setFormCategory] = useState('general');
  const [fields, setFields] = useState([]);
  const [editingFormId, setEditingFormId] = useState(null);

  const fetchForms = useCallback(async () => {
    try {
      const res = await axios.get('/forms');
      setForms(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const resetBuilder = () => {
    setFormTitle(''); setFormDesc(''); setFormCategory('general');
    setFields([]); setEditingFormId(null);
  };

  const addField = () => {
    setFields(prev => [...prev, {
      id: 'f_' + Date.now(), type: 'text', label: '', required: false, placeholder: '', options: []
    }]);
  };

  const updateField = (idx, key, val) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  };

  const removeField = (idx) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
  };

  const moveField = (idx, dir) => {
    const newFields = [...fields];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newFields.length) return;
    [newFields[idx], newFields[swapIdx]] = [newFields[swapIdx], newFields[idx]];
    setFields(newFields);
  };

  const saveForm = async () => {
    if (!formTitle.trim()) { toast.error('El título es requerido'); return; }
    if (fields.length === 0) { toast.error('Agrega al menos un campo'); return; }
    const invalidFields = fields.filter(f => !f.label.trim());
    if (invalidFields.length > 0) { toast.error('Todos los campos necesitan un nombre'); return; }

    const payload = {
      title: formTitle, description: formDesc, category: formCategory,
      fields: fields.map(f => ({
        ...f,
        options: (f.type === 'select' || f.type === 'multiselect') ? f.options : []
      })),
      is_active: true
    };

    try {
      if (editingFormId) {
        await axios.put(`/forms/${editingFormId}`, payload);
        toast.success('Formulario actualizado');
      } else {
        await axios.post('/forms', payload);
        toast.success('Formulario creado');
      }
      resetBuilder();
      setView('list');
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm('¿Eliminar este formulario y todas sus respuestas?')) return;
    try {
      await axios.delete(`/forms/${formId}`);
      toast.success('Formulario eliminado');
      fetchForms();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const toggleFormActive = async (form) => {
    try {
      await axios.put(`/forms/${form.id}`, { is_active: !form.is_active });
      fetchForms();
    } catch (err) { toast.error('Error'); }
  };

  const openEdit = (form) => {
    setFormTitle(form.title);
    setFormDesc(form.description || '');
    setFormCategory(form.category);
    setFields(form.fields.map(f => ({ ...f, options: f.options || [] })));
    setEditingFormId(form.id);
    setView('builder');
  };

  const openFill = (form) => {
    setSelectedForm(form);
    setView('fill');
  };

  const openResponses = async (form) => {
    setSelectedForm(form);
    try {
      const res = await axios.get(`/forms/${form.id}/responses`);
      setResponses(res.data);
    } catch (err) { setResponses([]); }
    setView('responses');
  };

  // === LIST VIEW ===
  if (view === 'list') {
    return (
      <div className="space-y-4" data-testid="forms-list">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" /> Formularios
          </h2>
          <Button onClick={() => { resetBuilder(); setView('builder'); }}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
            data-testid="create-form-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Formulario
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : forms.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-slate-400">
            <FileText className="w-16 h-16 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">No hay formularios</p>
            <p className="text-sm">Crea tu primer formulario personalizado</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map(form => (
              <Card key={form.id} className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all"
                data-testid={`form-card-${form.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-sm font-semibold text-slate-800 truncate">{form.title}</CardTitle>
                      {form.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{form.description}</p>}
                    </div>
                    <button onClick={() => toggleFormActive(form)} className="ml-2 flex-shrink-0" title={form.is_active ? 'Desactivar' : 'Activar'}>
                      {form.is_active
                        ? <ToggleRight className="w-6 h-6 text-green-500" />
                        : <ToggleLeft className="w-6 h-6 text-slate-300" />}
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">
                      {CATEGORIES.find(c => c.value === form.category)?.label || form.category}
                    </Badge>
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                      {form.fields?.length || 0} campos
                    </Badge>
                    <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">
                      {form.response_count || 0} respuestas
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => openFill(form)}
                      className="flex-1 text-xs border-slate-200" data-testid={`fill-form-${form.id}`}>
                      <Send className="w-3 h-3 mr-1" /> Llenar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openResponses(form)}
                      className="flex-1 text-xs border-slate-200" data-testid={`responses-form-${form.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Respuestas
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(form)}
                      className="text-xs border-slate-200"><Edit className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => deleteForm(form.id)}
                      className="text-xs border-slate-200 text-red-500 hover:bg-red-50"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // === BUILDER VIEW ===
  if (view === 'builder') {
    return (
      <div className="space-y-4" data-testid="form-builder">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { resetBuilder(); setView('list'); }}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <h2 className="text-lg font-bold text-slate-800">
            {editingFormId ? 'Editar Formulario' : 'Nuevo Formulario'}
          </h2>
        </div>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-600">Título del Formulario *</Label>
                <Input value={formTitle} onChange={e => setFormTitle(e.target.value)}
                  placeholder="Ej: Formulario de Inscripción" data-testid="form-title-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Categoría</Label>
                <Select value={formCategory} onValueChange={setFormCategory}>
                  <SelectTrigger data-testid="form-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Descripción</Label>
              <Textarea value={formDesc} onChange={e => setFormDesc(e.target.value)}
                placeholder="Descripción del formulario..." className="min-h-[60px]" />
            </div>
          </CardContent>
        </Card>

        {/* Fields */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Campos ({fields.length})</h3>
            <Button size="sm" onClick={addField} variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50" data-testid="add-field-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar Campo
            </Button>
          </div>

          {fields.length === 0 && (
            <Card><CardContent className="p-8 text-center text-slate-400 text-sm">
              Haz clic en "Agregar Campo" para comenzar a diseñar el formulario
            </CardContent></Card>
          )}

          {fields.map((field, idx) => (
            <Card key={field.id} className="bg-white border-slate-200" data-testid={`field-${idx}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-2">
                    <button onClick={() => moveField(idx, -1)} className="text-slate-400 hover:text-slate-600" disabled={idx === 0}>
                      <ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => moveField(idx, 1)} className="text-slate-400 hover:text-slate-600" disabled={idx === fields.length - 1}>
                      <ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-[10px] text-slate-500">Nombre del Campo *</Label>
                        <Input value={field.label} onChange={e => updateField(idx, 'label', e.target.value)}
                          placeholder="Ej: Nombre completo" className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">Tipo</Label>
                        <Select value={field.type} onValueChange={v => updateField(idx, 'type', v)}>
                          <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">Placeholder</Label>
                        <Input value={field.placeholder} onChange={e => updateField(idx, 'placeholder', e.target.value)}
                          placeholder="Texto de ejemplo" className="text-sm" />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                        <Checkbox checked={field.required}
                          onCheckedChange={v => updateField(idx, 'required', v)} />
                        Obligatorio
                      </label>
                    </div>
                    {(field.type === 'select' || field.type === 'multiselect') && (
                      <div>
                        <Label className="text-[10px] text-slate-500">Opciones (separar con Enter)</Label>
                        <Textarea value={(field.options || []).join('\n')}
                          onChange={e => updateField(idx, 'options', e.target.value.split('\n'))}
                          placeholder={"Opción 1\nOpción 2\nOpción 3"} className="min-h-[60px] text-sm" />
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeField(idx)} className="text-red-400 hover:text-red-600 pt-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => { resetBuilder(); setView('list'); }}>Cancelar</Button>
          <Button onClick={saveForm} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
            data-testid="save-form-btn">
            {editingFormId ? 'Guardar Cambios' : 'Crear Formulario'}
          </Button>
        </div>
      </div>
    );
  }

  // === FILL VIEW ===
  if (view === 'fill' && selectedForm) {
    return <FormFiller form={selectedForm} onBack={() => { setSelectedForm(null); setView('list'); }} onSubmitted={() => { setView('list'); fetchForms(); }} />;
  }

  // === RESPONSES VIEW ===
  if (view === 'responses' && selectedForm) {
    return (
      <div className="space-y-4" data-testid="form-responses">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedForm(null); setView('list'); }}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <h2 className="text-lg font-bold text-slate-800">Respuestas: {selectedForm.title}</h2>
          <Badge className="bg-purple-50 text-purple-700 border-purple-200">{responses.length} respuestas</Badge>
        </div>

        {responses.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-30" />
            No hay respuestas todavía
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {responses.map((resp, rIdx) => (
              <Card key={resp.id} className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">{resp.submitted_by_name}</Badge>
                      <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs capitalize">{resp.submitted_by_role}</Badge>
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(resp.created_at).toLocaleString('es-ES')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedForm.fields.map(field => {
                      const val = resp.answers[field.id];
                      return (
                        <div key={field.id} className="bg-slate-50 rounded-lg p-2.5">
                          <p className="text-[10px] text-slate-500 font-medium uppercase">{field.label}</p>
                          <p className="text-sm text-slate-800 mt-0.5">
                            {val === undefined || val === '' || val === null ? (
                              <span className="text-slate-300 italic">Sin respuesta</span>
                            ) : field.type === 'checkbox' ? (
                              val ? <Check className="w-4 h-4 text-green-600 inline" /> : <X className="w-4 h-4 text-red-400 inline" />
                            ) : field.type === 'multiselect' && Array.isArray(val) ? (
                              val.join(', ')
                            ) : field.type === 'file' && typeof val === 'object' ? (
                              <a href={val.path} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">{val.filename}</a>
                            ) : field.type === 'signature' ? (
                              val ? <img src={val} alt="Firma" className="h-12 border rounded" /> : 'Sin firma'
                            ) : String(val)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

// === FORM FILLER COMPONENT ===
const FormFiller = ({ form, onBack, onSubmitted }) => {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sigField, setSigField] = useState(null);

  const updateAnswer = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleFileUpload = async (fieldId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`/forms/${form.id}/responses/upload-file`, formData);
      updateAnswer(fieldId, { filename: res.data.filename, path: res.data.path, file_id: res.data.file_id });
      toast.success('Archivo subido');
    } catch (err) {
      toast.error('Error al subir archivo');
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    for (const field of form.fields) {
      if (field.required) {
        const val = answers[field.id];
        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
          toast.error(`El campo "${field.label}" es obligatorio`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`/forms/${form.id}/responses`, { form_id: form.id, answers });
      toast.success('Formulario enviado');
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="form-filler">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{form.title}</h2>
          {form.description && <p className="text-xs text-slate-500">{form.description}</p>}
        </div>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-5 space-y-5">
          {form.fields.map(field => (
            <div key={field.id} className="space-y-1.5" data-testid={`fill-field-${field.id}`}>
              <Label className="text-sm text-slate-700 font-medium">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </Label>

              {field.type === 'text' && (
                <Input value={answers[field.id] || ''} onChange={e => updateAnswer(field.id, e.target.value)}
                  placeholder={field.placeholder || ''} />
              )}
              {field.type === 'textarea' && (
                <Textarea value={answers[field.id] || ''} onChange={e => updateAnswer(field.id, e.target.value)}
                  placeholder={field.placeholder || ''} className="min-h-[80px]" />
              )}
              {field.type === 'number' && (
                <Input type="number" value={answers[field.id] || ''} onChange={e => updateAnswer(field.id, e.target.value)}
                  placeholder={field.placeholder || ''} />
              )}
              {field.type === 'date' && (
                <Input type="date" value={answers[field.id] || ''} onChange={e => updateAnswer(field.id, e.target.value)} />
              )}
              {field.type === 'select' && (
                <Select value={answers[field.id] || ''} onValueChange={v => updateAnswer(field.id, v)}>
                  <SelectTrigger><SelectValue placeholder={field.placeholder || 'Seleccionar...'} /></SelectTrigger>
                  <SelectContent>
                    {(field.options || []).filter(o => o.trim()).map((opt, i) => (
                      <SelectItem key={i} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {field.type === 'multiselect' && (
                <div className="space-y-1 border rounded-lg p-3 bg-slate-50">
                  {(field.options || []).filter(o => o.trim()).map((opt, i) => (
                    <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={(answers[field.id] || []).includes(opt)}
                        onCheckedChange={(checked) => {
                          const current = answers[field.id] || [];
                          updateAnswer(field.id, checked ? [...current, opt] : current.filter(v => v !== opt));
                        }}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
              {field.type === 'checkbox' && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={answers[field.id] || false}
                    onCheckedChange={v => updateAnswer(field.id, v)} />
                  {field.placeholder || 'Sí'}
                </label>
              )}
              {field.type === 'signature' && (
                <SignaturePad value={answers[field.id]} onChange={v => updateAnswer(field.id, v)} />
              )}
              {field.type === 'file' && (
                <div>
                  <input type="file" className="text-sm" onChange={e => e.target.files[0] && handleFileUpload(field.id, e.target.files[0])} />
                  {answers[field.id] && (
                    <p className="text-xs text-green-600 mt-1">Archivo: {answers[field.id].filename}</p>
                  )}
                </div>
              )}
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button variant="outline" onClick={onBack}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
              data-testid="submit-form-btn">
              {submitting ? 'Enviando...' : 'Enviar Formulario'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// === SIGNATURE PAD ===
const SignaturePad = ({ value, onChange }) => {
  const canvasRef = React.useRef(null);
  const [drawing, setDrawing] = useState(false);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const endDraw = () => {
    if (!drawing) return;
    setDrawing(false);
    onChange(canvasRef.current.toDataURL());
  };

  const clearSig = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onChange(null);
  };

  return (
    <div className="space-y-1">
      <canvas ref={canvasRef} width={400} height={120}
        className="border border-slate-300 rounded-lg bg-white cursor-crosshair w-full"
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
        style={{ touchAction: 'none' }} />
      <Button size="sm" variant="ghost" onClick={clearSig} className="text-xs text-slate-500">
        Limpiar firma
      </Button>
    </div>
  );
};

export default FormsTab;
